"use client";

import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { UserReportsTable } from "./UserReportsTable";
import { UserReportsSkeleton } from "./UserReportsSkeleton";
import { useAuth } from "@/contexts/AuthContext";
import { userReportsService } from "@/services/userReportsService";
import { toast } from "sonner";
import { DocumentData } from "firebase/firestore";
import { useTenancy } from "@/contexts/TenancyContext";
import { TOUR_STEP_IDS } from "@/lib/tour-constants";
import { TourAlertDialog, useTour } from "@/components/tour";

interface QuizAttempt {
  attemptNumber: number;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  timestamp: Date | null;
  passed: boolean;
}

interface PolicyQuizDetails {
  policyId: string;
  lastAttempt?: QuizAttempt;
  bestAttempt?: QuizAttempt;
  totalAttempts: number;
}

interface UserData {
  id: string;
  name: string;
  role: string;
  assignedPolicies: number;
  readingPercentage: number;
  quizPercentage: number;
  status: string;
  statusColor: string;
  policyDocs: DocumentData[];
  quizDetails: PolicyQuizDetails[];
  completedQuizzes: number;
  totalQuizzes: number;
}

export function UserReportsContent() {
  const { user } = useAuth();
  const { activeTenancy } = useTenancy();
  const [loading, setLoading] = useState(true);
  const [companyID, setCompanyID] = useState("");
  const [tenancyName, setTenancyName] = useState("");
  const [usersData, setUsersData] = useState<UserData[]>([]);

  // Tour state
  const { setSteps, isTourCompleted, setTourPage } = useTour();
  const [openTour, setOpenTour] = useState(false);

  useEffect(() => {
    if (!activeTenancy || !activeTenancy.id || ["", "Loading..."]?.includes(activeTenancy.id)) return;
    const fetchUserData = async () => {
      try {
        if (!user) return;

        // Get company ID from user document
        const companyID = activeTenancy?.id;

        if (!companyID) {
          throw new Error("User data not found or missing company ID");
        }

        setCompanyID(companyID);
        setLoading(true);

        // Use batch endpoint - single API call instead of N*M calls
        const batchData = await userReportsService.getBatchUserReports(companyID);

        if (!batchData) {
          throw new Error("Failed to fetch user reports data");
        }

        setTenancyName(batchData.companyInfo?.customerFriendlyName || '');

        // Process batch data into the format expected by the UI
        const processedUsers = batchData.users.map((batchUser) => {
          try {
            const policies = batchUser.policies || [];
            const policyCount = policies.length;
            
            // Calculate policy completion
            const completedPolicies = policies.filter(p => p.completed === "true").length;
            const completionPercentage = policyCount > 0
              ? Math.round((completedPolicies / policyCount) * 100)
              : 0;

            // Calculate quiz completion
            const policiesWithQuizzes = policies.filter(p => p.hasQuiz).length;
            const quizDetails: PolicyQuizDetails[] = [];
            
            for (const policy of policies) {
              if (!policy.hasQuiz || !policy.quizResults) continue;
              
              const policyId = policy.reference || policy.id;
              const quizResults = policy.quizResults;
              
              let lastAttempt: QuizAttempt | undefined;
              let bestAttempt: QuizAttempt | undefined;
              let totalAttempts = 0;
              let bestScore = 0;

              // Process each attempt
              for (const [attemptKey, attemptData] of Object.entries(quizResults)) {
                if (attemptKey === 'attempt_number') continue;
                
                const attempt = attemptData as any;
                const attemptNumber = parseInt(attemptKey.split('_')[1]) || 0;
                
                // Calculate total questions from the attempt data
                const questionIndices = new Set<number>();
                Object.keys(attempt).forEach(key => {
                  const match = key.match(/^(\d+)(?:CorrectAnswer|GivenAnswer|TimeAnswered)$/);
                  if (match) questionIndices.add(parseInt(match[1]));
                });
                
                const totalQuestions = questionIndices.size;
                let correctAnswers = 0;
                for (const q of questionIndices) {
                  if (attempt[`${q}CorrectAnswer`] === attempt[`${q}GivenAnswer`]) {
                    correctAnswers++;
                  }
                }
                
                const score = totalQuestions > 0 
                  ? Math.round((correctAnswers / totalQuestions) * 100) 
                  : 0;
                
                const quizAttempt: QuizAttempt = {
                  attemptNumber,
                  score,
                  correctAnswers,
                  totalQuestions,
                  timestamp: null, // Already processed on server
                  passed: correctAnswers >= Math.ceil(totalQuestions * 0.7)
                };

                totalAttempts++;
                if (score > bestScore) {
                  bestScore = score;
                  bestAttempt = quizAttempt;
                }
                lastAttempt = quizAttempt;
              }

              if (totalAttempts > 0) {
                quizDetails.push({ policyId, lastAttempt, bestAttempt, totalAttempts });
              }
            }

            const completedQuizzes = quizDetails.filter(q => q.bestAttempt?.passed).length;
            const quizPercentage = policiesWithQuizzes > 0
              ? Math.round((completedQuizzes / policiesWithQuizzes) * 100)
              : 0;  // DED-1899: Show 0% when no quizzes, not 100%

            // Determine status
            let status = "Incomplete";
            let statusColor = "destructive";
            if (completionPercentage === 100 && quizPercentage === 100) {
              status = "Completed";
              statusColor = "success";
            } else if (completionPercentage > 0 || quizPercentage > 0) {
              status = "In Progress";
              statusColor = "warning";
            }

            const userData = batchUser.userData || {};
            return {
              id: batchUser.userID,
              name: userData.displayName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.email?.split('@')[0] || 'Unknown User',
              role: batchUser.companyUserData?.ismsRole || 'User',
              assignedPolicies: policyCount,
              readingPercentage: completionPercentage,
              quizPercentage,
              status,
              statusColor,
              policyDocs: policies,
              quizDetails,
              completedQuizzes,
              totalQuizzes: policiesWithQuizzes
            };
          } catch (error) {
            console.error("Error processing user:", batchUser.userID, error);
            return null;
          }
        });

        setUsersData(processedUsers.filter(Boolean) as UserData[]);
      } catch (error: any) {
        console.error("Error fetching user data:", error);
        toast.error("Error loading user data");
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [user, activeTenancy]);

  // Tour implementation
  useEffect(() => {
    setTourPage('user-reports');
  }, [setTourPage]);

  // Tour configuration for User Reports
  useEffect(() => {
    const steps: any[] = [
      {
        content: (
          <div>
            <h3 className="font-semibold text-lg mb-2">User Reports</h3>
            <p>This is your user report page. It shows you reports for it.</p>
          </div>
        ),
        selectorId: TOUR_STEP_IDS.USER_REPORTS_PAGE,
        position: "bottom" as const,
      }
    ];

    // Search Bar
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Search Bar</h3>
          <p>This will search for users.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.USER_REPORTS_SEARCH_BAR,
      position: "bottom" as const,
    });

    // Filter
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Filter</h3>
          <p>This will filter the documents.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.USER_REPORTS_FILTER,
      position: "bottom" as const,
    });

    // Table Action
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Table Action</h3>
          <p>This will expand the users details.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.USER_REPORTS_TABLE_ACTION,
      position: "bottom" as const,
    });

    // Rows per page
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Rows per page</h3>
          <p>This will change the number of results per page.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.USER_REPORTS_ROWS_PER_PAGE,
      position: "left" as const,
    });

    // Page nav
    steps.push({
      content: (
        <div>
          <h3 className="font-semibold text-lg mb-2">Page nav</h3>
          <p>This will navigate to the next page.</p>
        </div>
      ),
      selectorId: TOUR_STEP_IDS.USER_REPORTS_PAGE_NAV,
      position: "left" as const,
    });

    console.log('User Reports tour steps:', steps.length);
    setSteps(steps);

    // Auto-start tour if not completed
    if (!isTourCompleted && usersData.length > 0) {
      setOpenTour(true);
    }
  }, [setSteps, isTourCompleted, setTourPage, usersData]);

  return (
    <div id={TOUR_STEP_IDS.USER_REPORTS_PAGE} className="page-container mx-auto px-4 py-8">
      <div className="glass-card">
        <div className="flex flex-col gap-6">
          <div>
            <h1 className="text-2xl font-bold">User Reports</h1>
            <p className="text-muted-foreground mt-1">
              View all of your organization's users' progress
            </p>
            {tenancyName && (
              <p className="text-sm text-muted-foreground">
                Organization: {tenancyName}
              </p>
            )}
          </div>

          <Card className="border-none shadow-none">
            <CardContent className="p-0 border-none shadow-none">
              {loading ? (
                <UserReportsSkeleton />
              ) : (
                <UserReportsTable users={usersData} />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <TourAlertDialog 
        isOpen={openTour} 
        setIsOpen={setOpenTour} 
      />
    </div>
  );
}
